import AsyncSelect from "react-select/async";
import debounce from "lodash.debounce";
import { useMemo, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ImageLink } from "../../../utils/constants";
import axiosInstance from "../../../axiosInterceptor";

const CheckboxOption = ({ data, innerRef, innerProps, selectProps }) => {
  const isSelected = selectProps.value.some((v) => v.value === data.value);
  const { onItemToggle } = selectProps;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={(e) => {
        e.preventDefault();
        onItemToggle(data, !isSelected);
      }}
      className="d-flex align-items-center p-2 rounded"
      style={{ transition: "background-color 0.2s", cursor: "pointer", gap: "12px" }}
    >
      <input type="checkbox" checked={isSelected} readOnly style={{ width: 15, height: 15 }} />
      {data.image && <img src={ImageLink(data.image)} alt={data.label} style={{ height: 70, width: 60, objectFit: "fill", borderRadius: 4 }} />}
      <div className="d-flex flex-column justify-content-center">
        <span className="fw-semibold">{data.label}</span>
        {data.sku && <small className="text-muted">{data.sku}</small>}
      </div>
    </div>
  );
};

const ProductMultiAsyncSelect = ({ value = [], onChange, parentId, endPoint, type }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  useEffect(() => {
    const preloadSelected = async () => {
      if (!value.length) return;
      const ids = value.map((v) => v.value).join(",");
      const res = await axiosInstance.get("/product-search", { params: { ids } });
      const options = res.data.data.map((item) => ({
        id: item.id,
        value: item.id,
        label: item.name,
        image: item.image,
        sku: item.sku,
      }));
      setSelectedOptions(options);
    };
    preloadSelected();
  }, [value]);

  const loadOptions = async (inputValue) => {
    if (!inputValue || !inputValue.trim()) return [];
    const res = await axiosInstance.get("/product-search", { params: { search: inputValue } });
    return res.data.data.map((item) => ({
      id: item.id,
      value: item.id,
      label: item.name,
      image: item.image,
      sku: item.sku,
    }));
  };

  const debouncedLoadOptions = useMemo(
    () =>
      debounce((inputValue, callback) => {
        loadOptions(inputValue).then(callback);
      }, 500),
    []
  );

  const toggleItem = async (item, shouldAdd) => {
    try {
      await axiosInstance.post(endPoint, { id: parentId, product_id: item.id, type });
      let newValue = shouldAdd ? [...value, item] : value.filter((v) => v.value !== item.value);
      newValue = newValue.filter((v, i, a) => a.findIndex((x) => x.value === v.value) === i);
      onChange(newValue);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <AsyncSelect
      isMulti
      cacheOptions
      defaultOptions={selectedOptions}
      loadOptions={debouncedLoadOptions}
      value={value}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      controlShouldRenderValue={false}
      placeholder="Search products..."
      components={{ Option: CheckboxOption }}
      onChange={(selected) => onChange(selected || [])}
      onItemToggle={toggleItem}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.label}
      styles={{
        control: (base) => ({ ...base, minWidth: 350, minHeight: 48, borderRadius: 8 }),
      }}
    />
  );
};

export default ProductMultiAsyncSelect;
